type FlagsMap = Readonly<Record<string, number>>

function parseFlags<TKey extends string>(
  map: Record<TKey, number>,
  value: number | string | null | undefined,
): number {
  if (value == null) return 0
  if (typeof value === 'number') return value
  return value
    .split(',')
    .map((token) => {
      const trimmed = token.trim()
      if (trimmed in map) return (map as Record<string, number>)[trimmed] as number
      const n = parseInt(trimmed, 10)
      return Number.isNaN(n) ? 0 : n
    })
    .reduce((acc, n) => acc | n, 0)
}

/**
 * An immutable bitmask value typed to a specific set of flag names.
 *
 * Construct via `createFlags(map).from(rawValue)` rather than directly.
 * Every mutating method returns a **new** instance — the original is unchanged.
 *
 * Serialises to a comma-separated string of active flag names via
 * `toString()` / `toJSON()`, making it safe to pass to `JSON.stringify`.
 *
 * @example
 * const pref = ContactPreference.from("Paper,Email")
 * pref.has("Email")               // true
 * pref.add("Sms").toString()      // "Paper,Email,Sms"
 * pref.remove("Paper").toNumber() // 6
 * JSON.stringify({ pref })        // '{"pref":"Paper,Email"}'
 */
export class FlagsValue<TKey extends string> {
  readonly #value: number
  readonly #map: Record<TKey, number>

  constructor(map: Record<TKey, number>, value: number | string | null | undefined) {
    this.#map = map
    this.#value = parseFlags(map, value)
  }

  /** Returns `true` when all bits of `flag` are set. */
  has(flag: TKey): boolean {
    const bit = this.#map[flag]
    return bit !== undefined && bit !== 0 && (this.#value & bit) === bit
  }

  /** Returns a new instance with `flag` added. */
  add(flag: TKey): FlagsValue<TKey> {
    const bit = this.#map[flag] ?? 0
    return new FlagsValue(this.#map, this.#value | bit)
  }

  /** Returns a new instance with `flag` removed. */
  remove(flag: TKey): FlagsValue<TKey> {
    const bit = this.#map[flag] ?? 0
    return new FlagsValue(this.#map, this.#value & ~bit)
  }

  /** Returns a new instance with `flag` toggled on or off. */
  toggle(flag: TKey): FlagsValue<TKey> {
    return this.has(flag) ? this.remove(flag) : this.add(flag)
  }

  /** Returns `true` when no flags are set. */
  isEmpty(): boolean {
    return this.#value === 0
  }

  /** Returns `true` when both values represent the same bitmask. */
  equals(other: FlagsValue<TKey> | number | string | null | undefined): boolean {
    const otherNum =
      other instanceof FlagsValue
        ? other.#value
        : parseFlags(this.#map, other as number | string | null | undefined)
    return this.#value === otherNum
  }

  /** Returns the raw numeric bitmask. Also used when coercing to a number (e.g. bitwise ops). */
  valueOf(): number {
    return this.#value
  }

  /** Returns the raw numeric bitmask. */
  toNumber(): number {
    return this.#value
  }

  /**
   * Returns a comma-separated string of active flag names, e.g. `"Paper,Email"`.
   * Returns the zero-value key (typically `"None"`) when no flags are set.
   * Used by `JSON.stringify` via `toJSON`.
   */
  toString(): string {
    if (this.#value === 0) {
      const noneKey = (Object.entries(this.#map) as [TKey, number][]).find(([, v]) => v === 0)?.[0]
      return noneKey ?? '0'
    }
    return (Object.entries(this.#map) as [TKey, number][])
      .filter(([, bit]) => bit !== 0 && (this.#value & bit) === bit)
      .map(([key]) => key)
      .join(',')
  }

  /** Called by `JSON.stringify` — produces the comma-separated flag string. */
  toJSON(): string {
    return this.toString()
  }
}

/**
 * Creates a typed flags descriptor from a plain map of flag names to bitmask values.
 * The descriptor acts like a C# `[Flags]` enum: it exposes the raw flag values
 * directly **and** provides `from` / `empty` to create typed `FlagsValue` instances.
 *
 * @example
 * // Define a flags type (do this once, in a types/constants file)
 * export const NotificationFlags = createFlags({
 *   None:  0,
 *   Push:  1,
 *   Email: 2,
 *   Sms:   4,
 * })
 * export type NotificationFlags = ReturnType<typeof NotificationFlags.from>
 *
 * // Construct from a raw backend value (number, string, or comma list)
 * const flags = NotificationFlags.from("Push,Email")
 * flags.has("Push")              // true
 * flags.add("Sms").toString()    // "Push,Email,Sms"
 * flags.remove("Push").toJSON()  // "Email,Sms"
 * flags.toNumber()               // 3
 *
 * // Access raw bitmask constants directly (useful with legacy hasFlag helper)
 * NotificationFlags.Push  // 1
 */
export function createFlags<TMap extends FlagsMap>(map: TMap) {
  type TKey = keyof TMap & string
  type Result = Readonly<TMap> & {
    of(...flags: TKey[]): FlagsValue<TKey>
    from(value: FlagsValue<TKey> | number | string | null | undefined): FlagsValue<TKey>
    empty(): FlagsValue<TKey>
  }

  return Object.freeze({
    ...map,
    /**
     * Constructs a `FlagsValue` from one or more **known** flag keys.
     * TypeScript will reject any key not defined in the map.
     *
     * @example
     * ContactPreferenceOptions.of("Paper", "Email")  // ✓
     * ContactPreferenceOptions.of("Typo")             // ✗ compile error
     */
    of(...flags: TKey[]): FlagsValue<TKey> {
      const value = flags.reduce((acc, key) => acc | ((map as Record<TKey, number>)[key] ?? 0), 0)
      return new FlagsValue(map as Record<TKey, number>, value)
    },
    /**
     * Parses any **raw wire value** (number, numeric string, named string, or
     * comma-separated list) into a `FlagsValue`. Use this at the API boundary.
     * For constructing from known keys in application code, prefer `of()`.
     * Passing an existing `FlagsValue` is a no-op.
     *
     * @example
     * ContactPreferenceOptions.from("Paper,Email")  // parse API string
     * ContactPreferenceOptions.from(3)              // parse numeric bitmask
     * ContactPreferenceOptions.from(rawApiValue)    // unknown wire value
     */
    from(value: FlagsValue<TKey> | number | string | null | undefined): FlagsValue<TKey> {
      if (value instanceof FlagsValue) return value
      return new FlagsValue(map as Record<TKey, number>, value)
    },
    /** Returns a `FlagsValue` with no flags set. */
    empty(): FlagsValue<TKey> {
      return new FlagsValue(map as Record<TKey, number>, 0)
    },
  } as unknown) as Result
}
